// Client portal — billing page. Shows current subscription state and
// links to the Stripe Customer Portal for self-service (cancel, swap
// plan, update payment method, download invoices).
import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, ExternalLink, Loader2, Receipt, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useAuth } from "@/lib/auth";
import { paymentsApi } from "@/lib/paymentsApi";

const BillingPortalPage = () => {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [params] = useSearchParams();
  const [pollState, setPollState] = useState(null);

  // After Stripe checkout redirect, poll the session status.
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    let cancelled = false;
    setPollState({ status: "polling" });
    const poll = async () => {
      attempts += 1;
      try {
        const r = await paymentsApi.checkoutStatus(sid);
        if (cancelled) return;
        if (r.payment_status === "paid") {
          setPollState({ status: "paid" });
          loadSub();
          return;
        }
        if (r.status === "expired") { setPollState({ status: "expired" }); return; }
        if (attempts >= 6) { setPollState({ status: "timeout" }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setPollState({ status: "error", message: e.message });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params]);

  const loadSub = async () => {
    setLoading(true);
    try {
      const r = await paymentsApi.mySubscription();
      setSub(r.subscription || null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!ready) return;
    if (!user) { navigate("/login?next=/portal/billing", { replace: true }); return; }
    loadSub();
  }, [ready, user, navigate]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await paymentsApi.portal();
      window.location.href = url;
    } catch (e) {
      window.alert(e.message);
    } finally { setPortalLoading(false); }
  };

  return (
    <Layout testId="page-portal-billing">
      <SEO title="Billing — geoVINUM" description="Manage your Academy subscription and invoices." />
      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-3">Portal</div>
          <h1 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] mb-8" data-testid="billing-title">Billing & subscriptions</h1>

          {pollState?.status === "polling" && (
            <Banner kind="info" testId="billing-pending">
              <Loader2 size={14} className="animate-spin" /> Confirming your payment…
            </Banner>
          )}
          {pollState?.status === "paid" && (
            <Banner kind="success" testId="billing-paid">
              <CheckCircle2 size={14} /> Payment confirmed. Welcome to Academy 🍷
            </Banner>
          )}
          {pollState?.status === "expired" && (
            <Banner kind="error" testId="billing-expired">Session expired. Please try again.</Banner>
          )}

          {loading ? (
            <div className="text-sm text-[color:var(--gv-ink)]/50">Loading…</div>
          ) : !sub ? (
            <NoSubscription />
          ) : (
            <ActiveSubscription sub={sub} onPortal={openPortal} portalLoading={portalLoading} />
          )}

          {/* Quick links to other portal sections */}
          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            <Link
              to="/portal/orders"
              className="block bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 hover:border-[color:var(--gv-pink)] transition-colors"
              data-testid="billing-link-orders"
            >
              <div className="font-display text-lg text-[color:var(--gv-ink)] mb-1">📦 Mes commandes posters</div>
              <div className="text-[12.5px] text-[color:var(--gv-ink)]/60">Suivi de production, expédition et livraison Gelato.</div>
            </Link>
            <Link
              to="/portal/academy"
              className="block bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 hover:border-[color:var(--gv-pink)] transition-colors"
              data-testid="billing-link-academy"
            >
              <div className="font-display text-lg text-[color:var(--gv-ink)] mb-1">🍷 geoVINUM Academy</div>
              <div className="text-[12.5px] text-[color:var(--gv-ink)]/60">Toutes les masterclasses et séries en streaming.</div>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const NoSubscription = () => (
  <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-8 text-center" data-testid="billing-no-sub">
    <Receipt size={28} className="mx-auto text-[color:var(--gv-pink)] mb-4" />
    <div className="font-display text-xl text-[color:var(--gv-ink)] mb-2">No active subscription</div>
    <p className="text-sm text-[color:var(--gv-ink)]/70 mb-5">
      Get unlimited access to geoVINUM Academy series for €19/month or €190/year.
    </p>
    <Link to="/academy#pricing" className="btn-primary inline-flex" data-testid="billing-see-plans">
      See plans <ArrowRight size={14} />
    </Link>
  </div>
);

const ActiveSubscription = ({ sub, onPortal, portalLoading }) => {
  const planLabel = sub.plan === "academy_monthly" ? "Academy — Monthly (€19 HT/mo)" : sub.plan === "academy_yearly" ? "Academy — Yearly (€190 HT/yr)" : sub.plan;
  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-8" data-testid="billing-active">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 size={18} className="text-emerald-500" />
        <div className="text-[11px] uppercase tracking-widest text-emerald-600">{sub.status || "active"}</div>
      </div>
      <div className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{planLabel}</div>
      {sub.current_period_end && (
        <div className="text-sm text-[color:var(--gv-ink)]/60 mb-6">
          Next renewal: {new Date(sub.current_period_end * 1000).toLocaleDateString()}
        </div>
      )}
      <button onClick={onPortal} disabled={portalLoading} className="btn-primary" data-testid="billing-portal-btn">
        {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
        Manage subscription
      </button>
    </div>
  );
};

const Banner = ({ kind = "info", children, testId }) => {
  const cls = kind === "success" ? "bg-emerald-50 text-emerald-800" : kind === "error" ? "bg-rose-50 text-rose-800" : "bg-sky-50 text-sky-800";
  return (
    <div className={`flex items-center gap-2 ${cls} px-4 py-3 rounded-[4px] text-sm mb-6`} data-testid={testId}>
      {children}
    </div>
  );
};

export default BillingPortalPage;
